import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  alpha,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarMonth as CalendarIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { useSnackbar } from 'notistack';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { GLTransaction, deleteGLTransaction } from '../firebase/glTransactionServices';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const Journal: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    id: ''
  });
  
  // State
  const [loading, setLoading] = useState(true);
  const [journalEntries, setJournalEntries] = useState<GLTransaction[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load journal entries
  useEffect(() => {
    fetchJournalEntries();
  }, [startDate, endDate]);
  
  const fetchJournalEntries = async () => {
    setLoading(true);
    try {
      let journalQuery = query(
        collection(db, 'glTransactions'),
        where('referenceType', '==', 'journal'),
        orderBy('transactionDate', 'desc')
      );
      
      // Apply date filters if provided
      if (startDate && endDate) {
        const startTimestamp = Timestamp.fromDate(startDate);
        const endTimestamp = Timestamp.fromDate(new Date(endDate.setHours(23, 59, 59, 999)));
        
        journalQuery = query(
          collection(db, 'glTransactions'),
          where('referenceType', '==', 'journal'),
          where('transactionDate', '>=', startTimestamp),
          where('transactionDate', '<=', endTimestamp),
          orderBy('transactionDate', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(journalQuery);
      const entries: GLTransaction[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        entries.push({
          id: doc.id,
          transactionDate: data.transactionDate,
          description: data.description,
          referenceType: data.referenceType,
          referenceId: data.referenceId,
          entries: data.entries
        });
      });
      
      setJournalEntries(entries);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      enqueueSnackbar('Failed to load journal entries', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle creating a new journal entry
  const handleCreateEntry = () => {
    navigate('/journal-entry');
  };
  
  // Handle viewing a journal entry
  const handleViewEntry = (id: string) => {
    navigate(`/gl-transactions/${id}`);
  };
  
  // Handle deleting a journal entry
  const handleDeleteEntry = async (id: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Journal Entry',
      message: 'Are you sure you want to delete this journal entry? This action cannot be undone.',
      id: id
    });
  };
  
  // Handle confirmation of deletion
  const handleConfirmDelete = async () => {
    try {
      // Delete the GL transaction and reverse its effects on account balances
      await deleteGLTransaction(confirmDialog.id);
      enqueueSnackbar('Journal entry deleted successfully', { variant: 'success' });
      fetchJournalEntries();
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      enqueueSnackbar('Failed to delete journal entry', { variant: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };
  
  // Handle cancel deletion
  const handleCancelDelete = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };
  
  // Handle search
  const handleSearch = () => {
    // Filter journal entries based on search term
    // This is client-side filtering since we've already loaded the data
    if (!searchTerm.trim()) {
      fetchJournalEntries();
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = journalEntries.filter(entry => 
      entry.description.toLowerCase().includes(searchTermLower) ||
      entry.referenceId?.toLowerCase().includes(searchTermLower) ||
      entry.entries.some(line => 
        line.accountName.toLowerCase().includes(searchTermLower) ||
        line.accountNumber.toLowerCase().includes(searchTermLower)
      )
    );
    
    setJournalEntries(filtered);
  };
  
  // Format date for display
  const formatDate = (date: Date | Timestamp): string => {
    if (date instanceof Date) {
      return format(date, 'dd MMM yyyy');
    } else if (date) {
      // For Firestore Timestamp objects
      const jsDate = date.toDate();
      return format(jsDate, 'dd MMM yyyy');
    }
    return '';
  };
  
  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Calculate total debits/credits for a journal entry
  const calculateTotals = (entry: GLTransaction) => {
    let totalDebit = 0;
    let totalCredit = 0;
    
    entry.entries.forEach(line => {
      totalDebit += line.debit || 0;
      totalCredit += line.credit || 0;
    });
    
    return { totalDebit, totalCredit };
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={600}>
            <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Journal Entries
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateEntry}
          >
            Create Journal Entry
          </Button>
        </Box>
        
        <Paper sx={{ mb: 3, p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="From Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }
              }}
            />
            <DatePicker
              label="To Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }
              }}
            />
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              onClick={handleSearch}
              sx={{ minWidth: '100px' }}
            >
              Search
            </Button>
          </Stack>
        </Paper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No journal entries found
                      </Typography>
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateEntry}
                        sx={{ mt: 1 }}
                      >
                        Create your first journal entry
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  journalEntries.map((entry) => {
                    const { totalDebit, totalCredit } = calculateTotals(entry);
                    return (
                      <TableRow 
                        key={entry.id}
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[100], 0.5) },
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) }
                        }}
                      >
                        <TableCell>{formatDate(entry.transactionDate)}</TableCell>
                        <TableCell>
                          <Tooltip title={entry.description} enterDelay={500}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {entry.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{entry.referenceId ? entry.referenceId : '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(totalDebit)}</TableCell>
                        <TableCell align="right">{formatCurrency(totalCredit)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => entry.id && handleViewEntry(entry.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => entry.id && handleDeleteEntry(entry.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCancelDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Journal;
